import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { CommonFormElementSize, PopupPlacement } from '@/common/types'
import DapDSRadioButton from '@/components/radio-button/radio-button.component'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'

import styles from '../input-group/input-group.scss?inline'

const KEY_CODES = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ']

/**
 * `dap-ds-radio-group`
 * @summary A radio group is a form element that allows the user to select one option from a set.
 * @element dap-ds-radio-group
 * @title - Radio group
 * @group radio-button
 *
 * @event dds-change - Fired when the radio group is checked.
 * @event dds-blur - Emitted when the radio group loses focus.
 * @event dds-focus - Emitted when the radio group gains focus.
 *
 * @property {string} name - The name of the radio group.
 * @property {string} value - The value of the radio group.
 * @property {boolean} disabled - Whether the radio group is disabled.
 * @property {boolean} required - Whether the radio group is required.
 * @property {string} label - The label of the radio group.
 * @property {string} description - The description of the radio group.
 * @property {string} tooltip - The tooltip of the radio group.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the radio group.
 * @property {string} feedback - The feedback of the radio group.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the radio group. Can be `negative`, `positive`, or `warning`.
 * @property {boolean} optional - The optional state of the radio group.
 * @property {string} optionalLabel - Text of optional label.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 * @property {'xs' | 'sm' | 'lg'} size - The size of the radio group. Default is `sm`.
 * @property {boolean} subtle - Font weight of the feedback label. Default is `false` which is bold.
 *
 * @slot - The content of the radio group.
 * @slot feedback-icon - The custom icon of the feedback.
 *
 * @csspart base - The main radio group container.
 * @csspart label - The label of the radio group.
 * @csspart tooltip - The tooltip of the radio group.
 * @csspart container - The container of the radio group items.
 */
@customElement('dap-ds-radio-group')
export default class DapDSRadioGroup extends GenericFormElement {
  /** The placement of the tooltip.
   * @type { 'top' | 'right' | 'bottom' | 'left' }
   */
  @property() tooltipPlacement: PopupPlacement = 'bottom'

  @query('#hidden') hiddenInput!: HTMLInputElement
  @property() optionalLabel = ''

  private _previousValue?: string

  static readonly styles = unsafeCSS(styles)

  willUpdate(changedProperties: Map<string, unknown>) {
    super.willUpdate(changedProperties)

    // Handle external value changes
    if (changedProperties.has('value')) {
      this._previousValue = changedProperties.get('value') as string
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    // Update radio button states when value changes from outside
    if (changedProperties.has('value') && this._previousValue !== this.value) {
      this.updateRadioStates()
    }
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ) {
    super.attributeChangedCallback(name, _old, value)
    if (name === 'disabled' || name === 'invalid') {
      this.getAllRadios().forEach(item => {
        item[name] = value !== null
      })
    }
  }

  private _handleSlotChange(event: Event) {
    const target = event?.target as HTMLInputElement
    target.setAttribute('slot', 'icon')
  }

  private getAllRadios() {
    return [...this.querySelectorAll<DapDSRadioButton>('dap-ds-radio-button')]
  }

  private updateRadioStates() {
    const radios = this.getAllRadios()

    radios.forEach(radio => {
      radio.checked = radio.value === this.value
      radio.size = this.size
    })

    // Update focusable state
    if (!this.value) {
      const firstFocusable = radios.find(radio => !radio.disabled)
      if (firstFocusable) {
        firstFocusable.focusable = true
      }
    }

    this.internals.setFormValue(this.value)
    this.setValidity()
  }

  private async syncRadioElements() {
    const radios = this.getAllRadios()

    await Promise.all(
      radios.map(async radio => {
        await radio.updateComplete
        radio.checked = radio.value === this.value
        radio.size = this.size
      }),
    )

    if (!this.value) {
      const firstFocusable = radios.find(radio => !radio.disabled)
      if (firstFocusable) {
        firstFocusable.focusable = true
      }
    }

    this.internals.setFormValue(this.value)
    this.setValidity()
  }

  private handleRadioClick(event: MouseEvent) {
    const target = (event.target as HTMLElement).closest<DapDSRadioButton>(
      'dap-ds-radio-button',
    )
    const radios = this.getAllRadios()
    const oldValue = this.value

    if (target!.disabled) {
      return
    }

    this.value = target!.value
    radios.forEach(radio => (radio.checked = radio === target))

    if (this.value !== oldValue) {
      this.emit('dds-change', { value: target!.value, checked: true })

      this.internals.setFormValue(this.value)
      this.setValidity()
    }
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (!KEY_CODES.includes(event.key)) {
      return
    }

    const radios = this.getAllRadios().filter(radio => !radio.disabled)
    const checkedRadio = radios.find(radio => radio.checked) ?? radios[0]
    const step =
      event.key === ' '
        ? 0
        : ['ArrowUp', 'ArrowLeft'].includes(event.key)
          ? -1
          : 1
    const oldValue = this.value
    let index = radios.indexOf(checkedRadio) + step

    if (index < 0) {
      index = radios.length - 1
    }

    if (index > radios.length - 1) {
      index = 0
    }

    this.getAllRadios().forEach(radio => {
      radio.checked = false
      radio.tabIndex = -1
    })

    this.value = radios[index].value
    radios[index].checked = true

    radios[index].tabIndex = 0
    radios[index].focus()

    if (this.value !== oldValue) {
      this.emit('dds-change', { value: radios[index].value, checked: true })

      this.internals.setFormValue(this.value)
      this.setValidity()
    }

    event.preventDefault()
  }

  setValidity(): void {
    const radios = this.getAllRadios()
    const isValid = radios.some(radio => radio.checked)

    if (this.required && !isValid) {
      this.internals.setValidity(
        { valueMissing: true },
        this.validationMessage,
        this.innerInput,
      )
    } else {
      this.internals.setValidity({})
    }
  }

  get validationMessage(): string {
    return ' '
  }

  checkValidity() {
    return !this.required || Boolean(this.required && this.value)
  }

  get validity() {
    return {
      ...this.internals.validity,
      valid: this.checkValidity(),
      valueMissing: this.required && !this.value,
    }
  }

  reportValidity() {
    return this.required ? this.checkValidity() : true
  }

  handleLabelClick() {
    const radios = this.getAllRadios()
    const checkedRadio = radios.find(radio => radio.checked) ?? radios[0]

    checkedRadio.focus()
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  render() {
    return html`
      <fieldset
        id="input"
        part="base"
        class="input-group"
        .disabled=${this.disabled}
        form=${ifDefined(this.form)}
        name=${this.name}
        role="radiogroup"
        tabindex="0"
        aria-labelledby="label"
        aria-required=${this.required}
        aria-describedby=${`description ${this.feedbackId}`}
        aria-errormessage=${ifDefined(this.feedbackId)}
        @invalid=${this.handleInvalid}>
        ${(this.label || this.tooltip) &&
        html`<legend class="input-group__legend">
          <div class="label-container">
            ${this.label &&
            html`<dap-ds-form-label
              part="label"
              id="label"
              .size=${this.size}
              .required=${this.required}
              .optionalLabel=${this.optionalLabel}
              .optional=${this.optional}
              .subtle=${this.subtle}
              .disabled=${this.disabled}
              .noMargin=${!!this.description}
              label=${ifDefined(this.label)}
              @click=${this.handleLabelClick}>
            </dap-ds-form-label>`}
            ${this.tooltip &&
            html` <dap-ds-tooltip
              part="tooltip"
              content=${this.tooltip}
              mode="toggle"
              placement=${this.tooltipPlacement}>
              <dap-ds-icon-button
                clean
                slot="trigger"
                class="info-button"
                aria-label=${this.tooltipAriaLabel}
                size=${this.size as 'xs' | 'sm' | 'md' | 'lg'}
                iconSize=${this.size === 'xs' ? '16' : '22'}>
                <dap-ds-icon
                  name="information-line"
                  class="info-button"></dap-ds-icon>
              </dap-ds-icon-button>
            </dap-ds-tooltip>`}
          </div>
          ${this.description &&
          html`
            <dap-ds-typography
              part="description"
              variant="description"
              class=${clsx('input-description')}
              size=${ifDefined(this.size as CommonFormElementSize)}
              id="description">
              ${this.description}
            </dap-ds-typography>
          `}
        </legend>`}
        <div part="container" class="input-group__items">
          <input id="hidden" hidden @invalid=${this.handleInvalid} />
          <slot
            @click=${this.handleRadioClick}
            @keydown=${this.handleKeyDown}
            @blur=${this.onBlur}
            @focus=${this.onFocus}
            @slotchange=${this.syncRadioElements}></slot>
        </div>
        ${this.feedback && this._renderFeedback()}
      </fieldset>
    `
  }

  private _renderFeedback() {
    return html`
      <dap-ds-feedback
        part="feedback"
        feedbackType=${ifDefined(this.feedbackType)}
        feedbackSize="${ifDefined(this.size as 'lg' | 'sm')}"
        id="feedback"
        .subtle=${this.subtle}>
        <slot
          name="feedback-icon"
          @slotchange="${this._handleSlotChange}"></slot>
        ${this.feedback}
      </dap-ds-feedback>
    `
  }
}
